// src/components/TopicSearchScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { locationData } from "../locationData";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

import "./TopicSearchScreen.css";

const TOPICS = [
  "Drugs",
  "Human Trafficking",
  "Immigration Issues",
  "Weapons",
  "Cartel Related",
  "Report a Tip",
];

function includesText(value, q) {
  if (!value || !q) return false;
  return String(value).toLowerCase().includes(String(q).toLowerCase());
}

/**
 * Build a canonical location key like:
 * National
 * Mexico Border|Texas
 * Mexico Border|Texas|El Paso County
 * Mexico Border|Texas|El Paso County|El Paso
 *
 * NOTE: This works best if you also STORE posts.locationKey in this format.
 * If you currently only store posts.location as a single string, you can adapt below.
 */
function buildLocationKey(border, state, county, city) {
  if (border === "National") return "National";
  const parts = [border];
  if (state) parts.push(state);
  if (county) parts.push(county);
  if (city) parts.push(city);
  return parts.join("|");
}

function TopicSearchScreen() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  // Topic + search
  const [selectedTopic, setSelectedTopic] = useState(TOPICS[0]);
  const [searchText, setSearchText] = useState("");
  const [sortMode, setSortMode] = useState("recent"); // recent | relevance

  // Location scope (same pattern as your HomeScreen)
  const [selectedBorder, setSelectedBorder] = useState("National");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  // Results
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Saved items map (topicPost:<postId> => docId)
  const [savedMap, setSavedMap] = useState({});

  // Guard
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  // Load saved list for this user
  useEffect(() => {
    async function loadSaved() {
      if (!user) return;
      try {
        const qSaved = query(
          collection(db, "team_members"),
          where("ownerId", "==", user.uid),
          where("memberType", "==", "topicPost")
        );
        const snap = await getDocs(qSaved);
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          map[`topicPost:${data.memberId}`] = d.id;
        });
        setSavedMap(map);
      } catch (e) {
        console.error(e);
      }
    }
    loadSaved();
  }, [user]);

  // Helpers for dropdowns
  const getStates = () => {
    if (selectedBorder === "National") return [];
    return Object.keys(locationData?.[selectedBorder]?.states || {});
  };

  const getCounties = () => {
    if (selectedBorder === "National" || !selectedState) return [];
    return Object.keys(
      locationData?.[selectedBorder]?.states?.[selectedState]?.counties || {}
    );
  };

  const getCities = () => {
    if (selectedBorder === "National" || !selectedState || !selectedCounty) return [];
    return (
      locationData?.[selectedBorder]?.states?.[selectedState]?.counties?.[selectedCounty] || []
    );
  };

  // When border changes, reset lower levels
  const handleBorderChange = (e) => {
    const v = e.target.value;
    setSelectedBorder(v);
    setSelectedState("");
    setSelectedCounty("");
    setSelectedCity("");
  };

  const handleStateChange = (e) => {
    const v = e.target.value;
    setSelectedState(v);
    setSelectedCounty("");
    setSelectedCity("");
  };

  const handleCountyChange = (e) => {
    const v = e.target.value;
    setSelectedCounty(v);
    setSelectedCity("");
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  // Fetch posts for current topic + location
  useEffect(() => {
    async function fetchPosts() {
      if (!user) return;

      setLoading(true);
      setErr("");

      try {
        const locKey = buildLocationKey(
          selectedBorder,
          selectedState,
          selectedCounty,
          selectedCity
        );

        // IMPORTANT:
        // Best practice: Store posts.topicTags as array and posts.locationKey as string.
        // Then query with array-contains + locationKey.
        //
        // If you DON'T have these fields yet, see the notes after the code.
        let qPosts;

        if (locKey === "National") {
          qPosts = query(
            collection(db, "posts"),
            where("approved", "==", true),
            where("topicTags", "array-contains", selectedTopic),
            orderBy("createdAt", "desc"),
            limit(100)
          );
        } else {
          qPosts = query(
            collection(db, "posts"),
            where("approved", "==", true),
            where("topicTags", "array-contains", selectedTopic),
            where("locationKey", "==", locKey),
            orderBy("createdAt", "desc"),
            limit(100)
          );
        }

        const snap = await getDocs(qPosts);
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setPosts(rows);
      } catch (e) {
        console.error(e);
        setErr(
          "Failed to fetch posts. If Firestore says 'requires an index', create the suggested composite index."
        );
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, [user, selectedTopic, selectedBorder, selectedState, selectedCounty, selectedCity]);

  // Client-side search + sort (extra layer)
  const filtered = useMemo(() => {
    const q = searchText.trim();
    let list = posts;

    if (q) {
      list = list.filter(
        (p) =>
          includesText(p.title, q) ||
          includesText(p.description, q) ||
          includesText(p.location, q) ||
          includesText(p.locationKey, q)
      );
    }

    if (sortMode === "relevance" && q) {
      list = [...list].sort((a, b) => {
        const aScore =
          (includesText(a.title, q) ? 3 : 0) +
          (includesText(a.description, q) ? 1 : 0);
        const bScore =
          (includesText(b.title, q) ? 3 : 0) +
          (includesText(b.description, q) ? 1 : 0);
        return bScore - aScore;
      });
    }

    return list;
  }, [posts, searchText, sortMode]);

  const isSaved = (postId) => Boolean(savedMap[`topicPost:${postId}`]);

  const toggleSave = async (postId) => {
    if (!user) return;

    const key = `topicPost:${postId}`;
    const existingDocId = savedMap[key];

    try {
      if (existingDocId) {
        await deleteDoc(doc(db, "team_members", existingDocId));
        setSavedMap((prev) => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      } else {
        const ref = await addDoc(collection(db, "team_members"), {
          ownerId: user.uid,
          memberType: "topicPost",
          memberId: postId,
          createdAt: serverTimestamp(),
        });
        setSavedMap((prev) => ({ ...prev, [key]: ref.id }));
      }
    } catch (e) {
      console.error(e);
      setErr("Could not update saved items. Try again.");
    }
  };

  const scopeLabel =
    selectedBorder === "National"
      ? "National"
      : selectedCity || selectedCounty || selectedState || selectedBorder;

  return (
    <div className="topic-container">
      <div className="topic-header">
        <h1>Topic Search</h1>

        <div className="topic-controls">
          <select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)}>
            {TOPICS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search in results…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
            <option value="recent">Most Recent</option>
            <option value="relevance">Relevance</option>
          </select>
        </div>
      </div>

      {/* Location Scope Filters */}
      <div className="topic-filters">
        <h3>Location Scope</h3>

        <div className="filter-row">
          <label>Border:</label>
          <select value={selectedBorder} onChange={handleBorderChange}>
            <option value="National">National</option>
            <option value="Mexico Border">Mexico Border</option>
            <option value="Canada Border">Canada Border</option>
          </select>
        </div>

        {selectedBorder !== "National" && (
          <div className="filter-row">
            <label>State:</label>
            <select value={selectedState} onChange={handleStateChange}>
              <option value="">-- Select State --</option>
              {getStates().map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedState && (
          <div className="filter-row">
            <label>County:</label>
            <select value={selectedCounty} onChange={handleCountyChange}>
              <option value="">-- Select County --</option>
              {getCounties().map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedCounty && (
          <div className="filter-row">
            <label>City:</label>
            <select value={selectedCity} onChange={handleCityChange}>
              <option value="">-- Select City --</option>
              {getCities().map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="topic-results">
        <h2>
          Results: <span className="muted">{selectedTopic}</span> •{" "}
          <span className="muted">{scopeLabel}</span>
        </h2>

        {err && <p className="error-text">{err}</p>}
        {loading && <p className="muted">Loading…</p>}

        {!loading && filtered.length === 0 ? (
          <p className="muted">No posts found.</p>
        ) : (
          <div className="results-list">
            {filtered.map((p) => (
              <div key={p.id} className="post-card">
                <div className="post-main">
                  <h4 className="post-title">{p.title || "Untitled"}</h4>
                  <div className="post-sub">
                    {p.location || p.locationKey || "Unknown location"}
                  </div>
                  <p className="post-desc">{p.description || ""}</p>
                </div>

                <div className="post-actions">
                  <button
                    className={isSaved(p.id) ? "btn saved" : "btn"}
                    onClick={() => toggleSave(p.id)}
                  >
                    {isSaved(p.id) ? "Saved ✓" : "Save"}
                  </button>

                  <button className="btn ghost" onClick={() => navigate(`/post/${p.id}`)}>
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="topic-footer">
        <button className="btn primary" onClick={() => navigate("/submit")}>
          Submit News
        </button>
      </div>
    </div>
  );
}

export default TopicSearchScreen;
